import {nanoid} from "nanoid"
import Geometry from "@fae/source/utility/Geometry.js"
import Timings from "@fae/source/engine/animating/utility/Timings.js"
import * as Motions from "@fae/source/engine/animating/utility/Motions.js"

export function BobMovement(unit) {
    if(unit == undefined) return []

    const SHAKE = 0.2
    const keyframes = [
        {"x": 0, "y": -SHAKE},
        {"x": 0, "y": SHAKE},
        {"x": 0, "y": -SHAKE},
        {"x": 0, "y": SHAKE},
    ]

    return keyframes.map((deltaposition, index) => {
        return {
            "mark": (index + 1) * 100,
            "unit": {
                "key": unit.key,
                "position": Geometry.add(unit.position, deltaposition)
            }
        }
    }).concat({
        "mark": (keyframes.length + 1) * 100,
        "unit": {
            "key": unit.key,
            "position": unit.position,
        }
    })
}

export function Shudder({unitKey, position, intensity, mark = 0}) {
    const SHUDDER = intensity ?? 0.1

    if(unitKey == undefined) return
    if(position == undefined) return

    return [
        {"x": 0, "y": -SHUDDER},
        {"x": 0, "y": SHUDDER},
        {"x": SHUDDER, "y": 0},
        {"x": -SHUDDER, "y": 0},
        {"x": SHUDDER, "y": -SHUDDER},
        {"x": -SHUDDER, "y": SHUDDER},
        {"x": SHUDDER, "y": -SHUDDER},
        {"x": -SHUDDER, "y": SHUDDER},
        {"x": 0, "y": -SHUDDER},
        {"x": 0, "y": SHUDDER},
        {"x": SHUDDER, "y": 0},
        {"x": -SHUDDER, "y": 0},
        {"x": SHUDDER, "y": -SHUDDER},
        {"x": -SHUDDER, "y": SHUDDER},
        {"x": SHUDDER, "y": -SHUDDER},
        {"x": -SHUDDER, "y": SHUDDER},
        {"x": 0, "y": 0},
    ].map((deltaposition, index) => {
        return {
            "mark": mark + (index * 25),
            "unit": {
                "key": unitKey,
                "position": Geometry.add(position, deltaposition)
            }
        }
    })
}

export function SwingBackMovement({mark, unitKey, position, direction}) {
    const backDirection = Geometry.multiply(direction, -0.25)
    const forwardDirection = Geometry.multiply(direction, +0.125)
    return [
        {
            "mark": mark + 0,
            "unit": {
                "key": unitKey,
                "position": {...position},
            }
        },
        {
            "mark": mark + 16,
            "unit": {
                "key": unitKey,
                "position": Geometry.add(position, backDirection),
            }
        },
        {
            "mark": mark + 133,
            "unit": {
                "key": unitKey,
                "position": Geometry.add(position, backDirection),
            }
        },
        {
            "mark": mark + 200,
            "unit": {
                "key": unitKey,
                "position": Geometry.add(position, forwardDirection),
            }
        },
        {
            "mark": mark + 200 + 33,
            "unit": {
                "key": unitKey,
                "position": Geometry.add(position, forwardDirection),
            }
        },
        {
            "mark": mark + 200 + 100,
            "unit": {
                "key": unitKey,
                "position": {...position},
            }
        },
    ]
}

export function NudgedMovement({mark, unitKey, direction, position}) {
    mark = mark ?? 0
    return [
        {
            "mark": mark + 0,
            "unit": {
                "key": unitKey,
                "position": position
            }
        },
        {
            "mark": mark + 100,
            "timing": Timings.easeInQuad,
            "unit": {
                "key": unitKey,
                "position": Geometry.add(position, Geometry.multiply(direction, 0.25)),
            }
        },
        {
            "mark": mark + 200, // 133
            "timing": Timings.easeInQuad,
            "unit": {
                "key": unitKey,
                "position": Geometry.add(position, {"x": 0, "y": 0})
            }
        },
    ]
}

export function PushedMovement({mark, endmark, unitKey, startPosition, endPosition}) {
    mark = mark ?? 0
    endmark = endmark ?? mark + 166
    const direction = Geometry.direction(startPosition, endPosition)
    return [
        {
            "mark": mark,
            "unit": {
                "key": unitKey,
                "position": startPosition,
            }
        },
        {
            "mark": endmark - 33,
            "timing": Timings.easeOutQuad,
            // "motion": Motions.arc(0.5),
            "unit": {
                "key": unitKey,
                "position": Geometry.add(endPosition, Geometry.multiply(direction, -0.25)),
            }
        },
        {
            "mark": endmark,
            "timing": Timings.easeInQuad,
            "unit": {
                "key": unitKey,
                "position": endPosition,
            }
        },
    ]
}

export function ArcedMovement({mark, endmark, unitKey, startPosition, endPosition}) {
    mark = mark ?? 0
    endmark = endmark ?? mark + 166
    return [
        {
            "mark": mark,
            "unit": {
                "key": unitKey,
                "position": startPosition,
            }
        },
        {
            "mark": endmark,
            "timing": Timings.easeOutQuad,
            "motion": Motions.arc(1.25),
            "unit": {
                "key": unitKey,
                "position": endPosition,
            }
        },
    ]
}

export function TextFloat({mark, text, value, font, position, type, color, unitKey}) {
    const key = nanoid()
    return [
        {
            "key": key,
            "type": "particle",
            "mark": mark,
            "text": {
                "string": text,
                "font": font || "numbers",
                "type": type,
                "unitKey": unitKey,
                "value": value,
            },
            "color": color,
            "position": Geometry.add(position, {"z": 0}),
        },
        {
            "key": key,
            "type": "particle",
            "mark": mark + 400,
            "timing": Timings.easeOutQuad,
            "position": Geometry.add(position, {"z": 1}),
        },

        {
            "key": key,
            "type": "particle",
            "mark": mark + 450,
            "opacity": 1,
        },
        {
            "key": key,
            "type": "particle",
            "mark": mark + 600,
            "timing": Timings.easeOutQuad,
            "opacity": 0,
        },
    ]
}

export function ImageFloat({mark, image, position}) {
    const key = nanoid()
    return [
        {
            "key": key,
            "type": "particle",
            "mark": mark,
            "image": image,
            "position": Geometry.add(position, {"z": 0}),
        },
        {
            "key": key,
            "type": "particle",
            "mark": mark + 400,
            "timing": Timings.easeOutQuad,
            "position": Geometry.add(position, {"z": 1}),
        },

        {
            "key": key,
            "type": "particle",
            "mark": mark + 450,
            "opacity": 1,
        },
        {
            "key": key,
            "type": "particle",
            "mark": mark + 800,
            "timing": Timings.easeOutQuad,
            "opacity": 0,
        },
    ]
}

export function JumpMovement({mark, unitKey, startPosition, endPosition}) {
    return [
        {
            "mark": mark,
            "unit": {
                "key": unitKey,
                "position": startPosition,
            }
        },
        {
            "mark": 200,
            "motion": Motions.arc(0.5),
            "timing": Timings.bezier(0, 1 - 0.2, 1, 0 + 0.2),
            "unit": {
                "key": unitKey,
                "position": endPosition,
            }
        }
    ]
}
